import type { OccurrenceSummaryItem } from '@models';

import { isNumericValue, isDurationValue } from './metric-value';

const buildMetricTotals = (summary: OccurrenceSummaryItem[]) => {
  const totals: Record<string, { formattedTotal: string; name: string }[]> = {};

  for (const { habitId, occurrences: habitOccurrences } of summary) {
    const [first] = habitOccurrences;
    const metricDefinitions = first.habit.metricDefinitions;

    const summableMetrics = metricDefinitions
      .filter((m) => {
        return m.type === 'number' || m.type === 'duration';
      })
      .sort((a, b) => {
        return a.sortOrder - b.sortOrder;
      });

    if (summableMetrics.length === 0) {
      continue;
    }

    const sums: { formattedTotal: string; name: string }[] = [];

    for (const metric of summableMetrics) {
      let sum = 0;
      let hasValues = false;

      for (const occ of habitOccurrences) {
        const mv = occ.metricValues.find((v) => {
          return v.habitMetricId === metric.id;
        });

        if (!mv) {
          continue;
        }

        const value = mv.value;

        if (metric.type === 'number' && isNumericValue(value)) {
          sum += value.numericValue;
          hasValues = true;
        } else if (metric.type === 'duration' && isDurationValue(value)) {
          sum += value.durationMs;
          hasValues = true;
        }
      }

      if (!hasValues) {
        continue;
      }

      let formattedTotal: string;

      if (metric.type === 'number') {
        const { config } = metric;
        const formatted =
          config.decimalPlaces != null
            ? sum.toFixed(config.decimalPlaces)
            : String(sum);

        formattedTotal = config.unit
          ? `${formatted} ${config.unit}`
          : formatted;
      } else {
        const { config } = metric;
        const totalSec = Math.floor(sum / 1000);
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;

        if (config.format === 'minutes') {
          formattedTotal = `${Math.floor(sum / 60000)} min`;
        } else if (config.format === 'seconds') {
          formattedTotal = `${totalSec} sec`;
        } else if (config.format === 'hours') {
          formattedTotal = `${(sum / 3600000).toFixed(1)} hr`;
        } else if (config.format === 'mm:ss') {
          const totalMinutes = Math.floor(totalSec / 60);
          formattedTotal = `${totalMinutes}m ${s}s`;
        } else if (config.format === 'hh:mm:ss') {
          formattedTotal = `${h}h ${m}m ${s}s`;
        } else {
          formattedTotal = `${h}h ${m}m`;
        }
      }

      sums.push({ formattedTotal, name: metric.name });
    }

    if (sums.length > 0) {
      totals[habitId] = sums;
    }
  }

  return totals;
};

export default buildMetricTotals;
